import { useCallback, useEffect, useState } from "react";
import api from "../../../utils/api";
import {
  ACCOUNT_STATUS,
  applyAccountStatusToSession,
  isExpiredAccount,
  isPaidAccount,
  isPremiumAccount,
  isTrialClockExpired,
  normalizeAccountStatus,
  trialDaysLeft,
} from "../../../lib/accountStatus";
import { persistAuthUser, getCurrentUser } from "../../../lib/authSession";

/**
 * Loads account_status (+ trial clock) from profile; refreshes on window focus.
 */
export function useClientAccountStatus() {
  const [accountStatus, setAccountStatus] = useState(() =>
    normalizeAccountStatus(getCurrentUser()?.account_status)
  );
  const [trialEndsAt, setTrialEndsAt] = useState(
    () => getCurrentUser()?.trial_ends_at || null
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const profile = await api.get("/api/users/profile/", { auth: true });
      persistAuthUser(profile);
      const status = normalizeAccountStatus(
        profile.account_status || ACCOUNT_STATUS.TRIAL
      );
      applyAccountStatusToSession(profile);
      setAccountStatus(status);
      setTrialEndsAt(profile.trial_ends_at || null);
      return status;
    } catch {
      const cached = normalizeAccountStatus(getCurrentUser()?.account_status);
      setAccountStatus(cached);
      setTrialEndsAt(getCurrentUser()?.trial_ends_at || null);
      return cached;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const clockExpired = isTrialClockExpired(trialEndsAt);
  const normalized = normalizeAccountStatus(accountStatus);
  // Client-side safety: if the clock is past, treat as expired even before
  // the next profile sync flips account_status on the server.
  const effectivelyExpired =
    isExpiredAccount(normalized) ||
    (normalized === ACCOUNT_STATUS.TRIAL && clockExpired);
  const effectivelyTrial =
    normalized === ACCOUNT_STATUS.TRIAL && !clockExpired;

  return {
    accountStatus: effectivelyExpired ? ACCOUNT_STATUS.EXPIRED : normalized,
    // Premium-only features (perks, free deletions, subscriber pricing).
    isPremium: isPremiumAccount(normalized),
    // Any active paid subscription (basic or premium).
    isPaid: isPaidAccount(normalized),
    isExpired: effectivelyExpired,
    isTrial: effectivelyTrial,
    trialEndsAt,
    trialDaysLeft: trialDaysLeft(trialEndsAt),
    loading,
    refresh,
  };
}

export default useClientAccountStatus;
